// src/components/Admin/TherapistCmp/TherapistForm/TherapistFormModal.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Button,
  Upload,
  message,
  Row,
  Col,
  Spin,
} from "antd";
import {
  UploadOutlined,
  UserOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TherapistReviApi } from "../../../../api/revi/therapistApi";
import { useAuth } from "../../../../hooks";

const api = new TherapistReviApi();

export function TherapistFormModal({
  visible,
  onClose,
  therapist,
  institutionId,
  onSuccess,
}) {
  // Nombre, apellidos, contrasena, subir y cancelar ya viven en el bloque
  // comun: aqui solo van los textos propios del alta de terapeuta.
  const { t } = useTranslation(["patient", "common"]);

  const [form] = Form.useForm();
  const { accessToken } = useAuth();

  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [fileList, setFileList] = useState([]);

  // ✅ Primitivas derivadas (evita "missing dependency: therapist")
  const therapistId = therapist?._id ?? null;
  const firstname0 = therapist?.firstname ?? "";
  const lastname0 = therapist?.lastname ?? "";
  const active0 = therapist?.active ?? true;
  const avatar0 = therapist?.avatar ?? null;

  const buildRemoteAvatarFileList = useCallback((avatarUrl) => {
    if (!avatarUrl) return [];
    return [
      {
        uid: "-1",
        name: "avatar",
        status: "done",
        url: avatarUrl,
      },
    ];
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!visible) return;

      // Limpieza fuerte siempre al abrir para no arrastrar valores
      form.resetFields();
      setFileList([]);

      // Defaults del listado -> email SIEMPRE vacío (porque no viene en listado)
      form.setFieldsValue({
        firstname: firstname0,
        lastname: lastname0,
        email: "",
        active: active0,
        password: "",
      });

      setFileList(buildRemoteAvatarFileList(avatar0));

      // Nuevo terapeuta: no hay detalle que cargar
      if (!therapistId) return;

      setLoadingProfile(true);
      try {
        const res = await api.getById(accessToken, therapistId);
        if (cancelled) return;

        const t = res?.therapist ?? {};
        const u = res?.user;

        const resolvedFirstname = t.firstname ?? firstname0;
        const resolvedLastname = t.lastname ?? lastname0;

        // Email/active/avatar “buenos” vienen del User asociado
        const resolvedEmail = (u?.email ?? "").trim();
        const resolvedActive = u?.active ?? t.active ?? active0;
        const resolvedAvatar = u?.avatar ?? t.avatar ?? avatar0;

        form.setFieldsValue({
          firstname: resolvedFirstname,
          lastname: resolvedLastname,
          email: resolvedEmail,
          active: resolvedActive,
          password: "",
        });

        setFileList(buildRemoteAvatarFileList(resolvedAvatar));
      } catch (e) {
        console.error(e);
        message.error(e.message || t("terapeutas.form.errorPerfil"));
        // El email ya quedó limpio ("") => no hay “memoria”
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    therapistId,
    accessToken,
    form,
    firstname0,
    lastname0,
    active0,
    avatar0,
    buildRemoteAvatarFileList,
    t,
  ]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("firstname", values.firstname);
      formData.append("lastname", values.lastname);
      formData.append("email", values.email);
      formData.append(
        "active",
        values.active !== undefined ? values.active : true,
      );

      if (values.password) {
        formData.append("password", values.password);
      }

      // Avatar: solo si se subió uno nuevo (originFileObj existe)
      const file = fileList?.[0]?.originFileObj;
      if (file) formData.append("avatar", file);

      if (therapistId) {
        await api.update(accessToken, therapistId, formData);
        message.success(t("terapeutas.form.actualizado"));
      } else {
        await api.create(accessToken, institutionId, formData);
        message.success(t("terapeutas.form.creado"));
      }

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      message.error(error.message || t("terapeutas.form.errorGuardar"));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeUpload = ({ fileList: newFileList }) => {
    // Nos quedamos con el último elemento:
    // - si seleccionas una nueva imagen, reemplaza la anterior (remota o local)
    // - si borras, se queda []
    setFileList(newFileList.slice(-1));
  };

  return (
    <Modal
      title={
        therapistId
          ? t("terapeutas.form.editarTitulo")
          : t("terapeutas.form.nuevoTitulo")
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={600}
    >
      <Spin spinning={loadingProfile}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ active: true }}
          preserve={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstname"
                label={t("common:fields.name")}
                rules={[
                  {
                    required: true,
                    message: t("terapeutas.form.nombreObligatorio"),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t("common:fields.name")}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="lastname"
                label={t("common:fields.surname")}
                rules={[
                  {
                    required: true,
                    message: t("terapeutas.form.apellidosObligatorio"),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t("common:fields.surname")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label={t("terapeutas.form.email")}
            rules={[
              {
                required: true,
                message: t("terapeutas.form.emailObligatorio"),
              },
              { type: "email", message: t("terapeutas.form.emailInvalido") },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t("terapeutas.form.emailPlaceholder")}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={
              therapistId
                ? t("terapeutas.form.passwordNueva")
                : t("common:fields.password")
            }
            rules={[
              {
                required: !therapistId,
                message: t("terapeutas.form.passwordObligatoria"),
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="******" />
          </Form.Item>

          <Row gutter={16} align="middle">
            <Col span={12}>
              <Form.Item label={t("terapeutas.form.foto")}>
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onChange={handleChangeUpload}
                  beforeUpload={() => false}
                  accept="image/*"
                  // Truco “replace”: dejamos maxCount=2 para que el botón NO se oculte,
                  // pero como controlamos fileList con slice(-1), siempre habrá 0 o 1.
                  maxCount={2}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>
                      {fileList.length
                        ? t("terapeutas.form.cambiarFoto")
                        : t("common:actions.upload")}
                    </div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="active" valuePropName="checked">
                <Checkbox>{t("terapeutas.form.activo")}</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={onClose}>{t("common:actions.cancel")}</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={saving}>
                {therapistId
                  ? t("terapeutas.form.guardarCambios")
                  : t("terapeutas.form.crear")}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
